import React from 'react';
import { clsx } from 'clsx';
import { CreditRisk } from '@types';
import { makeStyles } from '@mui/styles';

import { TableFiscalRow } from '@network';
import { moneyFormatter } from '@utils';


interface Props {
  risk: CreditRisk;
  title?: string;
  className?: string;
  twoColors?: boolean;
}

export const RiskTaxes = ({ risk, title, className, twoColors }: Props) => {
  const cls = useStyles();
  const { tableFiscal } = risk;

  if (!tableFiscal || !tableFiscal.headers || !tableFiscal.headers.length) {
    return null;
  }

  const rows = tableFiscal.rows;
  let incomeTotal = 0;
  let expensesTotal = 0;
  let expensesProfit = 0;
  let declarationIncome = 0;
  let declarationProfit = 0;

  for (const row of rows) {
    incomeTotal += +(row.incomeTotal || 0);
    expensesTotal += +(row.expensesTotal || 0);
    expensesProfit += +(row.expensesProfit || 0);
    declarationIncome += +(row.declarationIncome || 0);
    declarationProfit += +(row.declarationProfit || 0);
  }

  const accumulatedRow: TableFiscalRow = {
    year: 'Accumulated',
    incomeTotal,
    expensesTotal,
    expensesProfit,
    declarationIncome,
    declarationProfit,
  };

  const getValue = (value: number | string | null): string => {
    if (!value) {
      return '';
    } else {
      return typeof value === 'number' ? moneyFormatter.format(value || 0).toString() : value;
    }
  };

  return (
    <div className={clsx(className, 'risk-taxes-wg')}>
      <div className={clsx(cls.title, 'title')}>{title ? title : 'General tax income/expenses'}</div>
      <div className={cls.wrap}>
        <div className={clsx('row', cls.row, cls.categories)}>
          <div className={clsx(cls.category, cls.col, cls.col_1)} />
          <div className={clsx(cls.category, cls.col, cls.col_1)}>Income</div>
          <div className={clsx(cls.category, cls.col, cls.col_2)}>Expenses</div>
        </div>
        <div className={clsx('row', cls.row)}>
          {tableFiscal.headers.map((header, key) => (
            <div key={key} className={clsx(cls.header, cls.col, cls.col_1)}>
              {header.label}
            </div>
          ))}
        </div>
        <div>
          {tableFiscal.rows.map((row, idx) => (
            <div key={idx} className={clsx(cls.row, 'row')}>
              {tableFiscal.headers.map((header, key) => (
                <div key={key} className={clsx(
                  cls.content, !twoColors && cls.bg, cls.col, cls.col_1, 'col',
                )}>
                  {getValue(row[header.key])}
                </div>
              ))}
            </div>
          ))}
          <div className={clsx(cls.row, 'row')}>
            {tableFiscal.headers.map((header, key) => (
              <div key={key} className={clsx(cls.content, cls.col, cls.col_1, 'col')}>
                {getValue(accumulatedRow[header.key])}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

const useStyles = makeStyles({
  title: {
    margin: '25px 0 5px',
    textAlign: 'center',
    fontSize: 16,
    fontWeight: 600,
    color: 'var(--title-color)',
  },
  wrap: {
    border: '1px solid var(--primary-color)',
    borderRadius: 5,
    overflow: 'hidden',
  },
  row: {
    display: 'flex',
  },
  col: {
    backgroundColor: 'var(--background-color)',
  },
  col_1: {
    flex: '0 0 25%',
    maxWidth: '125%',
  },
  col_2: {
    flex: '0 0 50%',
    maxWidth: '50%',
  },
  categories: {
    backgroundColor: 'var(--primary-color)',
  },
  category: {
    minHeight: 34,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: 'var(--background-color)',
    fontSize: 12,
    fontWeight: 500,
    backgroundColor: 'var(--primary-color)',
    borderLeft: '1px solid var(--background-color)',
    '&:first-child': {
      borderLeft: 'none',
    },
  },
  header: {
    minHeight: 34,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: 12,
    fontWeight: 500,
    color: 'var(--primary-color)',
    borderLeft: '1px solid var(--primary-color)',
    '&:first-child': {
      borderLeft: 'none',
    },
  },
  bg: {
    backgroundColor: 'var(--foreground-color)',
  },
  content: {
    borderTop: '1px solid var(--primary-color)',
    borderLeft: '1px solid var(--primary-color)',
    '&:first-child': {
      borderLeft: 'none',
    },
    minHeight: 34,
    fontSize: 12,
    fontWeight: 400,
    color: 'var(--primary-color)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
});
